import SparkMD5 from 'spark-md5'

function getBlobSlice() {
  return (
    File.prototype.slice || (File.prototype as any).mozSlice || (File.prototype as any).webkitSlice
  )
}

/**
 * Generate MD5 hash from File or Blob using SparkMD5 (incremental hashing)
 * More memory efficient for large files
 */
export async function generateMD5Hash(file: File | Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const chunkSize = 2 * 1024 * 1024 // 2MB chunks
    const chunks = Math.ceil(file.size / chunkSize)
    const spark = new SparkMD5.ArrayBuffer()
    const fileReader = new FileReader()
    const blobSlice = getBlobSlice()
    let currentChunk = 0

    fileReader.onload = function (e) {
      if (e?.target?.result instanceof ArrayBuffer) {
        spark.append(e.target.result)
      }
      currentChunk++

      if (currentChunk < chunks) {
        loadNextChunk()
      } else {
        const computedHash = spark.end()
        resolve(computedHash)
      }
    }

    fileReader.onerror = function (e) {
      console.warn('read file error', e)
      reject(new Error('Failed to read file for hashing'))
    }

    function loadNextChunk() {
      const start = currentChunk * chunkSize
      const end = start + chunkSize >= file.size ? file.size : start + chunkSize
      const chunk = blobSlice.call(file, start, end)
      fileReader.readAsArrayBuffer(chunk)
    }

    loadNextChunk()
  })
}

/**
 * Generate MD5 hash from File or Blob with chunk list (for upload client compatibility)
 * Returns both MD5 and chunk array
 */
export async function generateMD5HashWithChunks(
  file: File | Blob,
  chunkSize: number = 2 * 1024 * 1024,
): Promise<{ md5: string; chunkList: Blob[] }> {
  return new Promise((resolve, reject) => {
    let currentChunk = 0
    const chunks = Math.ceil(file.size / chunkSize)
    const spark = new SparkMD5.ArrayBuffer()
    const fileReader = new FileReader()
    const blobSlice = getBlobSlice()
    const chunkList: Blob[] = []

    fileReader.onload = function (e) {
      if (e?.target?.result instanceof ArrayBuffer) {
        spark.append(e.target.result)
      }
      currentChunk++

      if (currentChunk < chunks) {
        loadNextChunk()
      } else {
        const computedHash = spark.end()
        resolve({ md5: computedHash, chunkList })
      }
    }

    fileReader.onerror = function (e) {
      console.warn('read file error', e)
      reject(e)
    }

    function loadNextChunk() {
      const start = currentChunk * chunkSize
      const end = start + chunkSize >= file.size ? file.size : start + chunkSize
      const chunk = blobSlice.call(file, start, end)
      chunkList.push(chunk)
      fileReader.readAsArrayBuffer(chunk)
    }

    loadNextChunk()
  })
}

/**
 * Generate MD5 hash from string using SparkMD5
 */
export async function generateMD5FromString(text: string): Promise<string> {
  return SparkMD5.hash(text)
}

/**
 * Generate MD5 hash from ArrayBuffer using SparkMD5
 */
export async function generateMD5FromBuffer(buffer: ArrayBuffer): Promise<string> {
  const spark = new SparkMD5.ArrayBuffer()
  spark.append(buffer)
  return spark.end()
}
